package com.klantenstop.web.pages.legal

import androidx.compose.runtime.Composable
import com.klantenstop.web.components.H1Heading
import com.klantenstop.web.components.MarkDownSpanText
import com.klantenstop.web.components.H2Heading
import com.klantenstop.web.components.layout.PageLayout
import com.klantenstop.web.components.layout.PageSeoConfig
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.styles.Typography
import com.materialkobweb.styles.MaterialColorVars
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.px

@Page("/algemene-voorwaarden")
@Composable
fun TermsAndConditionsPage() {
    val breakpoint = rememberBreakpoint()

    PageLayout(
        title = "Algemene voorwaarden van KlantenStop",
        seo = PageSeoConfig.Managed(
            description = "Lees de volledige algemene voorwaarden van KlantenStop: de regels voor gebruik van ons platform, gebruikersverantwoordelijkheden en aansprakelijkheid.",
            indexable = true,
            breadcrumbLabel = "Algemene voorwaarden",
        )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(
                    topBottom = if (breakpoint >= Breakpoint.MD) 64.px else 32.px,
                    leftRight = if (breakpoint >= Breakpoint.MD) 48.px else 20.px
                ),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(40.px)
        ) {
            // Header
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(900.px),
                verticalArrangement = Arrangement.spacedBy(12.px)
            ) {
                H1Heading(
                    modifier = Modifier.fillMaxWidth(),
                    text = "Algemene Voorwaarden",
                    color = MaterialColorVars.Primary.value()
                )
                SpanText(
                    modifier = Modifier
                        .fontSize(14.px)
                        .color(MaterialColorVars.OnSurfaceVariant.value()),
                    text = "Laatste update: 12 mei 2026"
                )
            }

            // Intro
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(900.px),
                verticalArrangement = Arrangement.spacedBy(12.px)
            ) {
                SpanText(
                    modifier = Modifier
                        .fillMaxWidth()
                        .fontSize(if (breakpoint >= Breakpoint.MD) 16.px else 14.px)
                        .lineHeight(if (breakpoint >= Breakpoint.MD) 26.px else 22.px)
                        .color(MaterialColorVars.OnSurface.value()),
                    text = "Deze Algemene Voorwaarden vormen een juridisch bindend contract tussen KlantenStop en u. " +
                            "Door onze Diensten (website, app en gerelateerde functies) te gebruiken, accepteert u deze voorwaarden. " +
                            "Als u niet akkoord gaat, dient u onmiddellijk te stoppen met het gebruik."
                )
            }

            // Section 1
            TermSection(
                breakpoint = breakpoint,
                number = "1",
                title = "Gebruikersaccounts en registratie",
                body = "Bij accountaanmaak gaat u akkoord met het verstrekken van nauwkeurige gegevens, " +
                        "het geheimhouden van uw wachtwoord, en het aanvaarden van verantwoordelijkheid voor alle activiteiten. " +
                        "U bent volledig verantwoordelijk voor de beveiliging van uw account. Wij behouden het recht voor om accounts " +
                        "op te schorten of te beëindigen als u deze voorwaarden schendt, illegaal handelt, of de diensten misbruikt."
            )

            // Section 2
            TermSection(
                breakpoint = breakpoint,
                number = "2",
                title = "Aanvaardbaar gebruik",
                body = "U gaat ermee akkoord de diensten niet te gebruiken voor: het plaatsen van valse of lasterlijke inhoud, het lastigvallen van anderen, " +
                        "illegale activiteiten, het schenden van intellectuele eigendomsrechten, het distribueren van malware, " +
                        "het omzeilen van beveiligingsmaatregelen, of fraude. U bent volledig verantwoordelijk voor alle inhoud die u plaatst. " +
                        "Door inhoud te plaatsen, verleent u KlantenStop toestemming om deze te gebruiken en te distribueren."
            )

            // Section 3
            TermSection(
                breakpoint = breakpoint,
                number = "3",
                title = "Recensies en beoordelingen",
                body = "Recensies moeten eerlijk, waarheidsgetrouw en gebaseerd op echte ervaring zijn. " +
                        "U mag geen nep-, misleidende of haatdragende reviews plaatsen. Persoonlijke contactgegevens zijn niet toegestaan. " +
                        "Wij behouden het recht voor om inhoud te modereren, te bewerken of te verwijderen zonder kennisgeving. " +
                        "Bedrijfseigenaars mogen niet proberen reviews te manipuleren, nep-reviews creëren, of gebruikers betalen voor positieve reviews."
            )

            // Section 4
            TermSection(
                breakpoint = breakpoint,
                number = "4",
                title = "Bedrijfseigenaars: profiel- en reviewbeheer",
                body = "Als bedrijfseigenaar gaat u akkoord met het verstrekken van nauwkeurige bedrijfsgegevens, " +
                        "het snel bijwerken van informatie, en de bestrijding van frauduleuze praktijken. " +
                        "Bij het reageren op reviews belooft u professioneel en beleefd te blijven. " +
                        "U mag geen valse informatie over concurrenten plaatsen, reviews niet door intimidatie proberen te verwijderen, " +
                        "of enige vorm van manipulatie toepassen."
            )

            // Section 5
            TermSection(
                breakpoint = breakpoint,
                number = "5",
                title = "Intellectuele eigendomsrechten",
                body = "De Diensten, inclusief software, ontwerp en inhoud (behalve gebruikersgegenereerde inhoud), zijn eigendom van KlantenStop. " +
                        "U ontvangt een beperkte licentie voor persoonlijk, niet-commercieel gebruik. U mag de Diensten niet reproduceren, " +
                        "wijzigen, reverse-engineeren, verkopen of verhuren. KlantenStop® en onze logo's zijn handelsmerken die niet zonder toestemming mogen worden gebruikt."
            )

            // Section 6
            TermSection(
                breakpoint = breakpoint,
                number = "6",
                title = "Afwijzing van garanties",
                body = "DE DIENSTEN WORDEN GELEVERD OP EEN \"AS-IS\" BASIS ZONDER GARANTIES. " +
                        "Wij garanderen NIET dat de Diensten ononderbroken, foutloos of veilig zijn, of geschikt zijn voor enig doel. " +
                        "Gebruikersgegenereerde inhoud vertegenwoordigt alleen de mening van auteurs, niet van KlantenStop. " +
                        "Wij zijn niet verantwoordelijk voor de prestaties van services van derden (kaarten, analytics)."
            )

            // Section 7
            TermSection(
                breakpoint = breakpoint,
                number = "7",
                title = "Beperking van aansprakelijkheid",
                body = "IN MAXIMAAL WETTELIJK TOEGESTANE MATE IS KLANTENSTOP NIET AANSPRAKELIJK VOOR: " +
                        "indirecte of gevolgschadevergoeding, verlies van winsten of gegevens, onvermogen de Diensten te gebruiken, " +
                        "of schade door inhoud van anderen. Onze totale aansprakelijkheid bedraagt maximaal €0,00 (nul euro), " +
                        "aangezien de Diensten gratis worden aangeboden en er geen betaalde producten of diensten beschikbaar zijn."
            )

            // Section 8
            TermSection(
                breakpoint = breakpoint,
                number = "8",
                title = "Schadevergoeding",
                body = "U gaat ermee akkoord KlantenStop, haar functionarissen en werknemers te vrijwaren van alle aanspraken, schade " +
                        "en uitgaven voortvloeiend uit: uw gebruik van de Diensten, uw inhoud of activiteiten, " +
                        "uw schending van deze voorwaarden, of uw schending van wettelijke bepalingen."
            )

            // Section 9
            TermSection(
                breakpoint = breakpoint,
                number = "9",
                title = "Externe links en diensten van derden",
                body = "De Diensten kunnen koppelingen naar externe websites bevatten. Wij onderschrijven deze sites niet en zijn niet verantwoordelijk " +
                        "voor hun inhoud, nauwkeurigheid of veiligheid. Controleer altijd hun privacybeleid en voorwaarden voordat u deze gebruikt."
            )

            // Section 10
            TermSection(
                breakpoint = breakpoint,
                number = "10",
                title = "Beschikbaarheid van diensten",
                body = "Wij werken eraan de Diensten continu beschikbaar te stellen, maar garanderen dit niet. " +
                        "Wij kunnen onderhoud uitvoeren, updates plaatsen of functies wijzigen of stopzetten zonder voorafgaande kennisgeving. " +
                        "Wij zijn niet verplicht de Diensten ​​voor onbepaalde tijd te onderhouden."
            )

            // Section 11
            TermSection(
                breakpoint = breakpoint,
                number = "11",
                title = "Geschillenbeslechting",
                body = "Deze voorwaarden worden beheerst door Belgisch recht. " +
                        "Alle geschillen vallen onder de exclusieve rechtsmacht van de rechtbanken van Brussel, België. " +
                        "Voor onofficiële geschillenbeslechting kunt u contact opnemen met: **info@klantenstop.be**. " +
                        "Alle rechtszaken moeten binnen één (1) jaar na het ontstaan van het geschil worden ingesteld."
            )

            // Section 12
            TermSection(
                breakpoint = breakpoint,
                number = "12",
                title = "Wijzigingen in voorwaarden",
                body = "Wij behouden het recht voor deze voorwaarden op elk moment te wijzigen. " +
                        "Wijzigingen worden van kracht na publicatie op de website of per e-mail. " +
                        "Voortgezet gebruik van de Diensten na wijzigingen vormt acceptatie van de bijgewerkte voorwaarden."
            )

            // Section 13 – Contact
            TermSection(
                breakpoint = breakpoint,
                number = "13",
                title = "Contactinformatie",
                body = "Voor vragen, klachten of schendingsmeldingen:\n\n" +
                        "**C.B. Connect I.T. B.V.**\n" +
                        "Hanegreefstraat 7, 3940 Eksel\n" +
                        "België\n\n" +
                        "E-mail: info@klantenstop.be\n" +
                        "Website: klantenstop.be\n\n" +
                        "**Gegevensbeschermingsautoriteit (privacyaangelegenheden)**\n" +
                        "Drukpersstraat 35, 1000 Brussel\n" +
                        "Website: www.gegevensbeschermingsautoriteit.be"
            )

            // Legal links row
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(900.px)
                    .padding(top = 16.px),
                horizontalArrangement = Arrangement.spacedBy(16.px, Alignment.CenterHorizontally),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    modifier = Modifier
                        .fontSize(13.px)
                        .color(MaterialColorVars.OnSurfaceVariant.value()),
                    text = "Zie ook:"
                )
                Link(
                    path = Navigation.Screen.PrivacyPolicy.route,
                    variant = UndecoratedLinkVariant,
                    modifier = Modifier
                        .fontSize(13.px)
                        .color(MaterialColorVars.Primary.value())
                ) {
                    SpanText("Privacybeleid")
                }
            }
        }
    }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

@Composable
private fun TermSection(
    breakpoint: Breakpoint,
    number: String,
    title: String,
    body: String
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .maxWidth(900.px),
        verticalArrangement = Arrangement.spacedBy(12.px)
    ) {
        H2Heading(typography = Typography.TitleLarge, text = "$number. $title", color = MaterialColorVars.Primary.value())

        MarkDownSpanText(
            modifier = Modifier
                .fillMaxWidth()
                .fontSize(if (breakpoint >= Breakpoint.MD) 15.px else 14.px)
                .lineHeight(if (breakpoint >= Breakpoint.MD) 26.px else 22.px)
                .color(MaterialColorVars.OnSurface.value()),
            text = body
        )
    }
}
